import { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { fmt } from '../utils/format';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';
import api from '../services/api';

// Extend adminAPI for Odoo
const odooAPI = {
  getConfig:     () => api.get('/admin/odoo/config'),
  saveConfig:    (d) => api.post('/admin/odoo/config', d),
  test:          () => api.post('/admin/odoo/test'),
  syncCustomers: () => api.post('/admin/odoo/sync-customers'),
  getQueue:      () => api.get('/admin/odoo/queue'),
  retry:         (id) => api.post(`/admin/odoo/queue/${id}/retry`),
};

export default function OdooPage() {
  const [tab, setTab] = useState('config');

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div>
            <h2>Odoo Integration</h2>
            <div className="page-subtitle">Odoo 18 ↔ Magnolia Quoter sync</div>
          </div>
        </div>
        <div className="page-body">
          <div className="tabs">
            {[['config','⚙️ Configuration'],['queue','📋 Sync Queue'],['guide','📖 Setup Guide']].map(([k,l]) => (
              <button key={k} className={`tab${tab===k?' active':''}`} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
          {tab === 'config' && <ConfigTab />}
          {tab === 'queue'  && <QueueTab />}
          {tab === 'guide'  && <GuideTab />}
        </div>
      </div>
    </div>
  );
}

// ── CONFIG TAB ────────────────────────────────────────────────
function ConfigTab() {
  const [form, setForm] = useState({
    odoo_url: '', db_name: '', username: '', api_key: '',
    sync_enabled: true, sync_customers_enabled: true,
  });
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [syncing, setSyncing]   = useState(false);

  useEffect(() => {
    odooAPI.getConfig().then(r => {
      if (r.data) setForm(f => ({ ...f, ...r.data }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await odooAPI.saveConfig(form);
      toast.success('Odoo config saved');
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await odooAPI.test();
      setTestResult(r.data);
    } catch { setTestResult({ ok: false, error: 'Request failed' }); }
    finally { setTesting(false); }
  };

  const handleSyncCustomers = async () => {
    setSyncing(true);
    try {
      const r = await odooAPI.syncCustomers();
      toast.success(`Synced ${r.data.synced} customers from Odoo`);
    } catch (err) { toast.error(err.response?.data?.error || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Odoo Connection</span>
          {testResult && (
            <span className={`badge ${testResult.ok ? 'badge-green' : 'badge-red'}`}>
              {testResult.ok ? `✅ Connected (Odoo ${testResult.server_version})` : `❌ ${testResult.error}`}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Odoo URL *</label>
          <input className="form-input" placeholder="https://yourcompany.odoo.com"
            value={form.odoo_url} onChange={e => set('odoo_url', e.target.value)} />
          <div className="form-hint">No trailing slash. Example: https://magnolia.odoo.com</div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Database Name *</label>
            <input className="form-input" placeholder="magnolia"
              value={form.db_name} onChange={e => set('db_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Username (email) *</label>
            <input className="form-input" type="email" placeholder="admin@magnoliadoors.com"
              value={form.username} onChange={e => set('username', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">API Key *</label>
          <input className="form-input" type="password" placeholder="••••••••••••••••"
            value={form.api_key} onChange={e => set('api_key', e.target.value)} />
          <div className="form-hint">Odoo → Settings → Technical → API Keys → New</div>
        </div>

        <hr className="divider" />

        <div className="flex gap-4" style={{ marginBottom: 16 }}>
          {[
            ['sync_enabled', 'Sync quotes to Odoo'],
            ['sync_customers_enabled', 'Sync customers from Odoo'],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2" style={{ cursor:'pointer', fontSize:13 }}>
              <input type="checkbox" checked={form[k]}
                onChange={e => set(k, e.target.checked)} />
              {l}
            </label>
          ))}
        </div>

        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={handleTest} disabled={testing}>
            {testing ? 'Testing…' : '🔌 Test Connection'}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Config'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Customer Sync</span>
          <span className="text-muted text-sm">Pull all contacts from Odoo → Magnolia</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
          Imports all customers with <code>customer_rank &gt; 0</code> from Odoo into Magnolia.
          Runs automatically daily. Run manually after first setup.
        </p>
        <button className="btn btn-primary" onClick={handleSyncCustomers} disabled={syncing}>
          {syncing ? 'Syncing…' : '↓ Sync Customers from Odoo Now'}
        </button>
      </div>
    </div>
  );
}

// ── QUEUE TAB ─────────────────────────────────────────────────
function QueueTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    odooAPI.getQueue().then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleRetry = async (id) => {
    await odooAPI.retry(id);
    toast.success('Queued for retry');
    load();
  };

  const STATUS_BADGE = {
    pending:    'badge-blue',
    processing: 'badge-orange',
    done:       'badge-green',
    failed:     'badge-red',
    skipped:    'badge-gray',
  };

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-header" style={{ padding: '16px 20px' }}>
        <span className="card-title">Sync Queue ({items.length})</span>
        <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refresh</button>
      </div>
      {loading ? <div className="loading-center"><div className="spinner" /></div>
      : items.length === 0 ? (
        <div className="empty-state">
          <h3>Queue is empty</h3>
          <p>All Odoo syncs are up to date.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Type</th><th>Record</th><th>Operation</th><th>Status</th><th>Attempts</th><th>Error</th><th></th></tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td><span className="badge badge-gray">{item.entity_type}</span></td>
                  <td style={{ fontWeight:600 }}>{item.entity_name || item.entity_id}</td>
                  <td className="text-muted">{item.operation}</td>
                  <td><span className={`badge ${STATUS_BADGE[item.status]||'badge-gray'}`}>{item.status}</span></td>
                  <td className="text-mono">{item.attempts}/{item.max_attempts}</td>
                  <td style={{ fontSize:11, color:'var(--red)', maxWidth:200 }}>{item.error_msg?.slice(0,80)}</td>
                  <td>
                    {item.status === 'failed' && (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleRetry(item.id)}>
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── GUIDE TAB ─────────────────────────────────────────────────
function GuideTab() {
  return (
    <div className="card" style={{ maxWidth: 680 }}>
      <h3 style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Odoo 18 Setup Guide</h3>

      {[
        ['Step 1 — Create API Key in Odoo',
          'Go to Odoo → Settings → Technical → API Keys → New Key.\nGive it a name like "Magnolia Quoter".\nCopy the key immediately — it won\'t be shown again.'],
        ['Step 2 — Enter config in Magnolia',
          'Go to Configuration tab above.\nEnter your Odoo URL, database name, admin email, and the API key.\nClick "Test Connection" to verify — you should see ✅ Connected.'],
        ['Step 3 — Sync customers',
          'Click "Sync Customers from Odoo Now".\nThis imports all your Odoo contacts into Magnolia.\nAfter this, new quotes will automatically find the customer in Odoo.'],
        ['Step 4 — Set up Odoo Webhook (for Odoo → Magnolia sync)',
          'In Odoo → Settings → Technical → Webhooks → New:\n• Name: Magnolia - Sale Order Changes\n• Model: Sale Order (sale.order)\n• Events: On Update\n• URL: https://your-domain.com/api/webhooks/odoo?token=YOUR_WEBHOOK_SECRET\n\nRepeat for res.partner (customer changes).\n\nThe webhook secret is in your Magnolia .env file as ODOO_WEBHOOK_SECRET.'],
        ['How it works day-to-day',
          '1. Vendor creates quote in Magnolia → SO created in Odoo immediately (1-2 sec)\n2. Odoo SO number becomes the Magnolia quote number (e.g. S00012201)\n3. Vendor adds lines in Magnolia → lines sync to Odoo SO in background (~30 sec)\n4. Vendor marks Won → Odoo SO confirmed automatically\n5. Vendor marks Lost → Odoo SO cancelled automatically\n6. If someone changes a customer in Odoo → Magnolia updates via webhook'],
      ].map(([title, body]) => (
        <div key={title} style={{ marginBottom: 20 }}>
          <div style={{ fontWeight:700, fontSize:13, color:'var(--accent)', marginBottom:6 }}>{title}</div>
          <div style={{ fontSize:12, color:'var(--text-2)', whiteSpace:'pre-line', lineHeight:1.8,
                        background:'var(--bg-3)', borderRadius:6, padding:'10px 14px' }}>
            {body}
          </div>
        </div>
      ))}
    </div>
  );
}
